import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../context/OnboardingContext';
import { apiRegisterSportive } from '../services/api';
import { SHOW_NOTIFICATIONS } from '../constants/featureFlags';
import { storePendingVerificationEmail } from '../utils/pendingVerification';
import { isPasswordValid } from '../utils/password';
import {
  joinSportsForApi, normalizeHeight, normalizeWeight, validateCycleDate,
  validateEmail, validateHeight, validateName, validateWeight,
} from '../utils/signupValidation';
import PageLayout from '../components/PageLayout';
import './OnboardingNotifications.css';

// Dernier filet avant l'envoi : chaque écran contrôle déjà sa saisie, mais une donnée
// peut avoir été restaurée du cache de session ou contournée (retour arrière, onglet
// rechargé). On renvoie alors vers l'écran fautif plutôt que de laisser l'API refuser.
const precheck = (formData) => {
  // L'email et le mot de passe ne sont jamais mis en cache (OnboardingContext) :
  // après un rechargement de page ils sont vides, il faut repasser par le 1er écran.
  if (!formData.email || !formData.password) {
    return {
      message: 'Votre session a expiré : merci de ressaisir votre email et votre mot de passe.',
      path: '/inscription',
      label: 'Reprendre au début',
    };
  }
  const checks = [
    [validateEmail(formData.email), '/inscription'],
    [isPasswordValid(formData.password) ? '' : 'Le mot de passe ne respecte pas les critères.', '/inscription'],
    [validateName(formData.firstName, 'Le prénom'), '/onboarding/prenom'],
    [validateName(formData.lastName, 'Le nom'), '/onboarding/prenom'],
    [formData.birthDate ? '' : 'La date de naissance est requise.', '/onboarding/naissance'],
    [validateHeight(formData.height), '/onboarding/taille'],
    [validateWeight(formData.weight), '/onboarding/poids'],
    [validateCycleDate(formData.lastPeriodDate), '/onboarding/cycle'],
    [validateCycleDate(formData.lastOvulationDate), '/onboarding/cycle'],
  ];
  const failed = checks.find(([msg]) => msg);
  return failed ? { message: failed[0], path: failed[1], label: 'Corriger' } : null;
};

const OnboardingNotifications = () => {
  const navigate = useNavigate();
  const { formData } = useOnboarding();
  const [submitting, setSubmitting] = useState(false);
  // { message, path?, label?, existingAccount? } — affiché au lieu de rediriger.
  const [failure, setFailure] = useState(null);

  const registerAndRedirect = async () => {
    if (submitting) return;
    setFailure(null);

    const blocking = precheck(formData);
    if (blocking) { setFailure(blocking); return; }

    let dateNaissance = null;
    if (formData.birthDate) {
      const [day, month, year] = formData.birthDate.split('/');
      if (day && month && year) {
        dateNaissance = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    const email = formData.email.trim();
    const height = normalizeHeight(formData.height);
    const weight = normalizeWeight(formData.weight);
    const payload = {
      mail: email,
      password: formData.password,
      prenom: formData.firstName.trim(),
      nom: (formData.lastName || formData.firstName).trim(),
      date_naissance: dateNaissance || undefined,
      taille: height || null,
      poids: weight || null,
      sport: joinSportsForApi(formData.sport),
      sports_list: formData.sport?.length ? formData.sport : null,
      niveau: formData.level || null,
      contraception: formData.contraception || null,
      // Facultatives : elles amorcent la prédiction du cycle dès l'inscription.
      derniere_regles: formData.lastPeriodDate || null,
      derniere_ovulation: formData.lastOvulationDate || null,
      consent_accepted: formData.consentAccepted,
    };

    setSubmitting(true);
    try {
      await apiRegisterSportive(payload);
    } catch (err) {
      setSubmitting(false);
      // On reste sur cet écran avec le vrai motif. Auparavant on enchaînait sur la
      // saisie du code même en cas d'échec : l'inscription semblait réussie, aucun
      // code n'arrivait jamais, et la personne réessayait à l'aveugle.
      if (err.status === 409) {
        setFailure({
          message: `Un compte existe déjà avec l'adresse ${email}.`,
          existingAccount: true,
        });
      } else if (err.status === undefined) {
        setFailure({ message: 'Connexion impossible. Vérifiez votre réseau puis réessayez.' });
      } else {
        setFailure({ message: err.message });
      }
      return;
    }

    // Un email de vérification doit être validé avant le premier login (2FA).
    storePendingVerificationEmail(email);
    navigate('/onboarding/verification', { state: { email } });
  };

  const failureBlock = failure && (
    <div className="auth-notice" role="alert">
      <p className="auth-notice__text">{failure.message}</p>
      {failure.existingAccount && (
        <div className="button-group">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => navigate('/verification', { state: { email: formData.email.trim() } })}
          >
            J'ai reçu un code
          </button>
          <button type="button" className="btn btn--outline" onClick={() => navigate('/connexion')}>
            Se connecter
          </button>
        </div>
      )}
      {failure.path && (
        <div className="button-group">
          <button type="button" className="btn btn--outline" onClick={() => navigate(failure.path)}>
            {failure.label}
          </button>
        </div>
      )}
    </div>
  );

  // Tant que les notifications ne sont pas réellement implémentées (cf.
  // SHOW_NOTIFICATIONS / NON-IMPLEMENTE.md), cet écran final ne promet plus de
  // rappels : il se contente de finaliser la création de compte.
  if (!SHOW_NOTIFICATIONS) {
    return (
      <PageLayout step={7}>
        <div className="notifications-icon">🌸</div>

        <h2 className="title">Tout est prêt !</h2>
        <p className="subtitle">
          Nous finalisons la création de votre compte. Vous pourrez commencer
          votre suivi personnalisé juste après la vérification de votre email.
        </p>

        {failureBlock}

        <button className="btn btn--secondary" onClick={registerAndRedirect} disabled={submitting}>
          {submitting ? 'Création en cours…' : 'Créer mon compte'}
        </button>
      </PageLayout>
    );
  }

  return (
    <PageLayout step={7}>
      <div className="notifications-icon">🔔</div>

      <h2 className="title">Restez informée</h2>
      <p className="subtitle">
        Recevez des rappels pour vos entrées quotidiennes et des conseils
        personnalisés pour optimiser votre bien-être.
      </p>

      {failureBlock}

      <button className="btn btn--secondary" onClick={registerAndRedirect} disabled={submitting}>
        Autoriser les notifications
      </button>

      <button
        className="link--subtle notifications-later"
        onClick={registerAndRedirect}
        disabled={submitting}
      >
        Peut-être plus tard
      </button>
    </PageLayout>
  );
};

export default OnboardingNotifications;
